import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { ArrowRight, CheckCircle, Shield, Truck, Users } from 'lucide-react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/SliderStyles.css';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    fade: true,
    arrows: false,
  };

  const slides = [
    {
      title: "혁신을 이끄는 힘",
      description: "맞춤형 하네스 케이블과 제어 시스템으로 제조 산업의 미래를 선도합니다.",
      bgClass: "bg-slate-900",
      image: `${process.env.PUBLIC_URL}/images/banner1.jpg`
    },
    {
      title: "완벽한 품질 관리",
      description: "엄격한 품질 공정과 테스트를 통해 최고의 신뢰성을 보장합니다.",
      bgClass: "bg-blue-900",
      image: `${process.env.PUBLIC_URL}/images/banner2.jpg`
    },
  ];

  return (
    <div className="w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] text-white">
        <Slider {...heroSettings} className="h-full hero-slider">
          {slides.map((slide, index) => (
            <div key={index} className="relative h-screen min-h-[600px]">
              <div
                className={`absolute inset-0 bg-cover bg-center ${slide.bgClass}`}
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-transparent"></div>
              </div>
              <div className="relative h-full section-container flex flex-col justify-center">
                <div className="max-w-3xl space-y-6 animate-slide-up">
                  <h1 className="text-5xl md:text-7xl font-display font-bold leading-tight text-white">
                    {slide.title}
                  </h1>
                  <p className="text-xl md:text-2xl text-white max-w-2xl font-light">
                    {slide.description}
                  </p>
                  <div className="pt-8 flex flex-wrap gap-4">
                    <Link to="/products" className="bg-primary hover:bg-primary-light text-white px-8 py-4 rounded-full font-bold transition-all transform hover:scale-105 shadow-lg shadow-primary/30 flex items-center">
                      제품 보기 <ArrowRight className="ml-2" size={20} />
                    </Link>
                    <Link to="/quote" className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold transition-all border border-white/30">
                      문의하기
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </section>

      {/* Stats Section */}
      {/* <section className="py-20 bg-white">
        <div className="section-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: '설립일', value: '2002', suffix: '년' },
              { label: '누적 프로젝트', value: '500', suffix: '+' },
              { label: '파트너사', value: '100', suffix: '+' },
              { label: '고객 만족도', value: '99', suffix: '%' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-6 border-r last:border-r-0 border-slate-100">
                <div className="text-4xl md:text-5xl font-display font-bold text-primary mb-2">
                  {stat.value}<span className="text-2xl text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-slate-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Services Section */}
      <section className="py-24 bg-slate-50">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-secondary font-bold tracking-wider uppercase text-sm">Our Solutions</span>
            <h2 className="text-4xl font-display font-bold text-slate-900 mt-2 mb-4">핵심 기술 및 제품</h2>
            <p className="text-slate-600 text-lg">
              고객의 요구사항에 최적화된 맞춤형 솔루션으로 생산 효율을 극대화합니다.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100">
              <div className="h-64 bg-slate-200 relative overflow-hidden">
                <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/0 transition-all"></div>
                {/* Placeholder for Product Image */}
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/harness_thumb.jpg`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => (e.target as HTMLImageElement).src = 'https://placehold.co/600x400/e2e8f0/64748b?text=Harness'}
                    alt="Harness"
                  />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-3 text-slate-900">하네스 케이블</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  다양한 산업 환경에 대응하는 고내구성 맞춤형 하네스 케이블 어셈블리.
                </p>
                <Link to="/harness" className="inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors">
                  자세히 보기 <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100">
              <div className="h-64 bg-slate-200 relative overflow-hidden">
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/controlbox_thumb.jpg`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => (e.target as HTMLImageElement).src = 'https://placehold.co/600x400/e2e8f0/64748b?text=Control+Box'}
                    alt="Control Box"
                  />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-3 text-slate-900">전장/제어 박스</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  스마트 팩토리 및 자동화 설비를 위한 고성능 컨트롤 박스 설계 및 제작.
                </p>
                <Link to="/controlbox" className="inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors">
                  자세히 보기 <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Card 3 - Why Us */}
            <div className="bg-primary text-white rounded-2xl shadow-xl p-10 flex flex-col justify-center relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-3xl font-display font-bold mb-6">Why Power Tech?</h3>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <CheckCircle className="text-accent mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <strong className="block text-lg mb-1">정밀한 품질 관리</strong>
                      <span className="text-slate-300 text-sm">ISO 인증 및 자체 QA 프로세스 가동</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Truck className="text-accent mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <strong className="block text-lg mb-1">신속한 납기 준수</strong>
                      <span className="text-slate-300 text-sm">최적화된 생산 라인으로 빠른 대응</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Users className="text-accent mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <strong className="block text-lg mb-1">전문 엔지니어링</strong>
                      <span className="text-slate-300 text-sm">20년 경력의 전문가 그룹</span>
                    </div>
                  </li>
                </ul>
                <div className="mt-10">
                  <Link to="/about" className="bg-white text-primary px-6 py-3 rounded-lg font-bold hover:bg-slate-100 transition-colors inline-block">
                    회사 소개 더보기
                  </Link>
                </div>
              </div>
              {/* Decoration */}
              <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-12 translate-y-12">
                <Shield size={300} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand/Certifications Ticker (Optional - can be added here) */}

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900">
          {/* Abstract Background Graphic */}
          <div className="absolute top-0 left-0 w-full h-full opacity-20 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-400 via-slate-900 to-black"></div>
        </div>
        <div className="section-container relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">프로젝트를 시작할 준비가 되셨나요?</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            귀사의 요구사항에 맞는 최적의 견적을 받아보세요. 전문가가 친절하게 상담해 드립니다.
          </p>
          <Link to="/quote" className="inline-flex items-center bg-accent hover:bg-accent-hover text-white text-lg font-bold px-10 py-5 rounded-full shadow-2xl transition-all transform hover:-translate-y-1">
            무료 견적 요청하기 <ArrowRight className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
